import { DocumentCard, DocumentCardDetails, IStackItemStyles, IStackTokens, Stack } from "@fluentui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import AdvancedSearch from "../advancedSearch/advancedSearch";
import { ModeEnum } from "../src/taskpane/components/enums/modeEnum";
import Loader from "../src/taskpane/components/Loader";
import { RootState } from "../src/taskpane/components/_v2/redux/storeMain";
import { IAuthAccount, IAuthTokenResultDto } from "../src/taskpane/interfaces/IAuthTokenResultDto";
import { IUserProfile } from "../src/taskpane/interfaces/IUserProfile";
import { dataSourceItem, TenantState } from "../src/taskpane/redux/models/reduxModels";
import { getApprovals } from "../src/taskpane/redux/modules/approvals";
import { setMode } from "../src/taskpane/redux/modules/mode";
import { getTenantInfo, setTenantInfo } from "../src/taskpane/redux/modules/tenant";
import { getEmailToMetaDataMapping, setUserAccount, setUserProfile } from "../src/taskpane/redux/modules/user";
import authModule from "../utilities/AuthModule";
import { LanguageContext } from "../utilities/LocalizationModule";
import { UserModule } from "../utilities/UserModule";
import ContentBodyDirectory from "./Body/ContentBodyDirectory";
import { ContentManagerBody } from "./Body/ContentManagerBody";
import { ContentManagerHeader } from "./ContentManagerHeader";
import { ContentManagerDirectoryEnum } from "../src/taskpane/components/enums/ContentManagerDirectoryEnum";
import { registerHistoryEntry, resetContentManagerDirectory, setContentManagerDirectory, setContentManagerFilterValue, setContentManagerSearchValue, setContentManagerSelectedHomeSection, setIsContentManagerContentLoading, setIsContentManagerSideMenuCollapsed, toggleContentManagerReload } from "../src/taskpane/redux/modules/contentManagerDirectory";
import { resetWorkingDirectories, setOneDriveDetails, setWorkingDirectories } from "../src/taskpane/redux/modules/workingDirectory";
import { logger } from "../utilities/logger";
import { DocubirdCache, contentManager } from "../src/taskpane/config/constants";
import { getDriveItemByWebUrl, getListItemWebUrl, getOneDriveRootDetails, getTeam, getTeamChannelInfo, getTeamChannels } from "../src/taskpane/services/GraphService";
import { IDirectoryBuilder } from "../src/taskpane/interfaces/IDirectoryBuilder";
import { IOneDriveRootDetails } from "../src/taskpane/interfaces/IOneDriveRootDetails";
import { SearchedOneDriveDirectoryBuilder } from "../src/taskpane/components/helpers/searchedOneDriveDirectoryBuilder";
import { SearchedSharepointFoldersDirectoryBuilder } from "../src/taskpane/components/helpers/searchedSharepointFoldersDirectoryBuilder";
import { ComponentTypes, Initiallocations } from "../src/taskpane/config/config";
import LZString = require("lz-string");
import { StorageHelper } from "../utilities/StorageHelper";
import { tryDecodeUri, tryDecodeUriComponent } from "../utilities/EncodeDecode";
import { TrialRequest } from "../src/taskpane/components/TrialRequest";
import { LandingPageEnum } from "../src/taskpane/components/enums/LandingPageEnum";
import { HookType, IHookResult } from "../src/taskpane/components/_v2/redux/api/local/api-local-interfaces";
import { CacheHelper } from "../utilities/CacheHelper";
import { apiGetHooksByType } from "../src/taskpane/services/ApiService";
import { ContentManagerHomeEnum } from "../src/taskpane/components/enums/ContentManagerHomeEnum";
import { ScreenSize, useWindowSize } from "./Hooks/useWindowSize";
import { DatasourceTypeEnum } from "../src/taskpane/components/enums/datasourceTypeEnum";
import { getSharepointLibraryFiles } from "../src/taskpane/services/SharePointRestServices";
import { NewDocumentTemplate } from "../src/taskpane/interfaces/IDocumentTemplate";
import { IPageSettings } from "../src/taskpane/interfaces/IPageSettings";
import { signalCacheUpdate } from "../src/taskpane/redux/modules/cacheUpdate";
import { getLocalStorage, localStorageKeys, setLocalStorage } from "../src/taskpane/components/helpers/localStorageHelper";
import { ITeamChannelDto } from "../src/taskpane/interfaces/services/ITeamChannelDto";
import { ITeamChannelDocumentLibrary } from "../src/taskpane/interfaces/services/ITeamChannelDocumentLibrary";
import { getRelativeDocumentUrl } from "../src/taskpane/components/helpers/stringHelper";

const outerStackTokens: IStackTokens = {
    childrenGap: 20
};

const directoryContainerCollapsedStackItemStyles: IStackItemStyles = {
    root: {
        height: "calc(100vh - 50px - 10px)", // Subtract header height and padding
        width: 35,
        minWidth: 35,
        borderRight: "1px solid #edebe9",
        overflowY: "auto",
        paddingTop: 10,
        marginLeft: 10,
        marginRight: 10
    }
};

const draggableBorderStyles: React.CSSProperties = {
    borderLeft: "1px solid #edebe9",
    height: "calc(100vh - 50px)",
    width: 5,
    cursor: "col-resize",
    marginRight: 5,
    marginLeft: 0
};

interface IPreloadLocation {
    id: string;
    driveId: string;
    webUrl: string;
    siteId: string;
    type?: string;
    isTeams?: boolean;
    teamId?: string;
    channelId?: string;
}

export interface IContentManagerProps {
    account: IAuthAccount;
    initialMode: ModeEnum;
}

export const ContentManager = ({ account, initialMode }: IContentManagerProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const dispatch = useDispatch();

    const mode = useSelector((state: RootState) => state.mode.mode);
    const workingDir = useSelector((state: RootState) => state.workingDirectories.workingDirectories);
    const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const user = useSelector((state: RootState) => state.user);
    const tenantInfo = useSelector((state: RootState) => state.tenantInfo);
    const isSideMenuCollapsed = useSelector((state: RootState) => state.contentManagerDirectory.isSideMenuCollapsed);
    const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);
    const selectedHomeSection = useSelector((state: RootState) => state.contentManagerDirectory.selectedHomeSection);
    // const isOverlayVisible = useSelector((state: RootState) => state.overlay.isVisible);

    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [userAuthData, setUserAuthData] = React.useState<IAuthTokenResultDto>(undefined);

    const [isResizing, setIsResizing] = React.useState(false);
    const [width, setWidth] = React.useState(350);

    const windowSize = useWindowSize();

    const isSideMenuCollapsedRef = React.useRef<boolean>(false);
    const screenSizedRef = React.useRef<ScreenSize>(windowSize.screenSize);

    const directoryContainerStackItemStyles: IStackItemStyles = React.useMemo(() => {
        return {
            root: {
                height: "calc(100vh - 50px - 10px)", // Subtract header height and padding
                minWidth: width,
                width: width,
                overflowY: "auto",
                paddingTop: 10,
                "@media only screen and (max-width: 479px)": { minWidth: "100vw", width: "100vw" },
            }
        };
    }, [width]);

    const loadUserProfile = React.useCallback(async (account: IAuthAccount): Promise<IUserProfile> => {
        const userProfile: IUserProfile = await UserModule.getUserProfile(account);
        if (userProfile) {
            if (userProfile.settings?.enableApprovals) {
                const flowtoken: string = await authModule.getFlowAccessToken();
                if (flowtoken) {
                    dispatch(getApprovals(account.tenantId));
                }
            }
            authModule.setTenantApiKey(userProfile.tenantApiKey);
            dispatch(setUserProfile(userProfile));
            setLocalStorage(localStorageKeys.userProfile, userProfile);
        }
        else {
            authModule.signOut();
        }
        return userProfile;
    }, []);

    const loadTenantInfo = React.useCallback(async (account: IAuthAccount): Promise<TenantState> => {
        let tenantInfo: TenantState;
        if (account?.tenantId) {
            tenantInfo = await getTenantInfo(account.tenantId);
            if (tenantInfo) {
                dispatch(setTenantInfo(tenantInfo));
                setLocalStorage(localStorageKeys.tenantInfo, tenantInfo);
                await getDatasourceDocumentTemplates(tenantInfo.datasources);
            }
        }
        // dispatch(getSites(tenantInfo?.allowedSiteCollections, null, true, true));
        // dispatch(getTeams(true, true));
        return tenantInfo;
    }, []);

    const setSigningEnabled = React.useCallback(async (tenantId: string): Promise<void> => {
        let signingEnabled: boolean = false;
        let signalREnabled: boolean = false;
        try {
            const hooks = await apiGetHooksByType({ TenantGUID: tenantId, HookTypeId: HookType.AllAutomation });
            if (Array.isArray(hooks) && hooks.length > 0) {
                signingEnabled = hooks.filter(h => h.typeId === HookType.Sign).length > 0;
                signalREnabled = hooks.findIndex(h => h.typeId === HookType.SharePointItemCreate || h.typeId === HookType.SharePointItemMetadataEdit) !== -1;
                CacheHelper.set(DocubirdCache.hooks, hooks);
                setLocalStorage(localStorageKeys.automationHooks, hooks);
            }
        } catch (error) {
            // Ignore
        }
        CacheHelper.set(DocubirdCache.signingEnabled, signingEnabled);
        CacheHelper.set(DocubirdCache.signalREnabled, signalREnabled);
    }, []);

    const getDatasourceDocumentTemplates = React.useCallback(async (datasources: dataSourceItem[]): Promise<void> => {
        try {
            const dsDocTemplates: NewDocumentTemplate[] = [];

            if (Array.isArray(datasources) && datasources.length > 0) {
                await Promise.all(datasources
                    .filter(ds => ds.type.id === DatasourceTypeEnum.SharePointDocumentTemplate)
                    .map(async ds => {
                        try {
                            const templateDocuments = await getSharepointLibraryFiles(ds.url, ds.name);
                            if (templateDocuments?.length > 0) {
                                dsDocTemplates.push(...templateDocuments
                                    .filter(template => template)
                                    .map(template => ({ ...template, datasource: ds }))
                                );
                            }
                        } catch (error) {
                            logger(error, "ContentManager.tsx (getDatasourceDocumentTemplates)");
                        }
                    }));
            }

            if (dsDocTemplates.length > 0) {
                CacheHelper.set(DocubirdCache.datasourceDocumentTemplates, dsDocTemplates.sort((a, b) => a.title?.toUpperCase() < b.title?.toUpperCase() ? -1 : 1));
                dispatch(signalCacheUpdate());
            }
        } catch (error) {
            // Ignore
        }
    }, []);

    const preloadLocation = React.useCallback(async (account: IAuthAccount, oneDriveDetails: IOneDriveRootDetails, preload: string, preloadURL: string | undefined): Promise<boolean> => {
        try {
            if (preload) {
                if (preload === ContentManagerDirectoryEnum.SharePoint.toString() || preload === ContentManagerDirectoryEnum.Teams.toString()) {
                    dispatch(setContentManagerDirectory(preload === ContentManagerDirectoryEnum.SharePoint.toString() ? ContentManagerDirectoryEnum.SharePoint : ContentManagerDirectoryEnum.Teams));
                    return true;
                }
                else {
                    const preloadLocationDetails = JSON.parse(LZString.decompressFromEncodedURIComponent(preload));
                    if (preloadLocationDetails) {
                        if (Array.isArray(preloadLocationDetails)) {
                            dispatch(setWorkingDirectories(preloadLocationDetails));
                            return true;
                        }
                        else if (preloadLocationDetails.driveId && preloadLocationDetails.itemId) {
                            const locationWebUrl = await getListItemWebUrl(await authModule.getGraphAccessToken(), preloadLocationDetails.driveId, preloadLocationDetails.itemId);
                            if (locationWebUrl && (preloadLocationDetails.siteId || locationWebUrl.startsWith(account?.oneDriveWebUrl))) {
                                const preloadLocation: IPreloadLocation = {
                                    id: preloadLocationDetails.itemId,
                                    driveId: preloadLocationDetails.driveId,
                                    webUrl: locationWebUrl,
                                    siteId: preloadLocationDetails.siteId,
                                    type: preloadLocationDetails.type,
                                    isTeams: preloadLocationDetails.isTeams,
                                    teamId: preloadLocationDetails.teamId,
                                    channelId: preloadLocationDetails.channelId
                                }

                                if (preloadLocation.teamId && preloadLocation.channelId) {
                                    await preloadTeamChannelLocationStructure(preloadLocation.teamId, preloadLocation.channelId);
                                }
                                else {
                                    await preloadLocationStructure(account, preloadLocation, oneDriveDetails);
                                }
                                return true;
                            }
                        }
                    }
                }
            }
            else if (preloadURL) {
                const decodedWebURL: string = tryDecodeUriComponent(preloadURL);
                if (decodedWebURL) {
                    const driveItem = await getDriveItemByWebUrl(decodedWebURL);
                    if (driveItem) {
                        const isDocument: boolean = driveItem.file && !driveItem.folder;
                        const itemUrlResult = new URL(isDocument
                            ? driveItem.webUrl
                            : driveItem.listItemWebUrl ?? driveItem.webUrl
                        );

                        if (itemUrlResult) {
                            const decodedHref: string = tryDecodeUri(itemUrlResult.href);
                            const preloadLocation: IPreloadLocation = {
                                id: driveItem.id,
                                driveId: driveItem.driveId,
                                webUrl: decodedHref,
                                siteId: driveItem.siteId
                            }
                            await preloadLocationStructure(account, preloadLocation, oneDriveDetails);
                            return true;
                        }
                    }
                    else {
                        try {
                            // Ensure absolute url
                            const redirectionUrl = new URL(decodedWebURL);
                            window.location.replace(redirectionUrl);
                            return true;
                        } catch (error) {
                            // Ignore
                        }
                    }
                }
            }
        } catch (error) {
            logger(error, "ContentManager.tsx (preloadLocation)");
        }
        return false;
    }, []);

    const preloadTeamChannelLocationStructure = React.useCallback(async (teamId: string, channelId: string): Promise<void> => {
        dispatch(setIsContentManagerContentLoading(true));

        try {
            const selectedTeam: any = await getTeam(teamId);
            if (selectedTeam) {
                const directories: IPageSettings[] = [
                    {
                        type: ComponentTypes.Teams,
                        id: "2",
                        directory: "",
                        displayName: ComponentTypes.Teams,
                        webUrl: "",
                    },
                    {
                        id: selectedTeam.id,
                        type: ComponentTypes.SharePointDocuments,
                        siteId: selectedTeam.siteId ?? selectedTeam.id,
                        siteDisplayName: selectedTeam.displayName,
                        webUrl: selectedTeam.webUrl,
                        directory: "",
                        DocumentLibrary: "",
                        teamGroupId: selectedTeam.id,
                        teamDriveId: selectedTeam.teamDriveId,
                        displayName: selectedTeam.displayName,
                        isTeamsFolder: true,
                        isHub: selectedTeam.isHub,
                        hubId: selectedTeam.hubId,
                        parentHubId: selectedTeam.parentHubId
                    }
                ];
                try {
                    const teamChannels: ITeamChannelDto[] = await getTeamChannels(await authModule.getGraphAccessToken(), selectedTeam.id, selectedTeam.teamDriveId);

                    const selectedChannel: ITeamChannelDto = channelId && teamChannels?.find(channel => channel.id === channelId);
                    if (selectedChannel) {
                        const teamChannelInfo: ITeamChannelDocumentLibrary = await getTeamChannelInfo({ channelId: selectedChannel.id, driveId: selectedTeam.teamDriveId, groupId: selectedTeam.id, isPrivate: selectedChannel.isPrivate, isShared: selectedChannel.isShared });
                        if (teamChannelInfo) {
                            directories.push({
                                id: selectedChannel.isShared ? teamChannelInfo.driveId : teamChannelInfo.driveItemId,
                                parentId: selectedChannel.isShared ? null : teamChannelInfo.driveId,
                                type: ComponentTypes.SharePointFolders,
                                siteDisplayName: selectedTeam.displayName,
                                webUrl: teamChannelInfo.sharePointSiteUrl,
                                fileTitle: selectedChannel.displayName,
                                directory: getRelativeDocumentUrl(teamChannelInfo.driveItemWebUrl, selectedChannel.isShared),
                                DocumentLibrary: teamChannelInfo.documentLibraryName,
                                displayName: selectedChannel.displayName,
                                isTeamsRootFolder: true,
                                isTeamsFolder: true,
                                isSharedTeamsChannel: selectedChannel.isShared,
                                sharePointListId: teamChannelInfo.sharePointListId,
                                siteId: teamChannelInfo.siteId,
                                teamChannelId: selectedChannel.id
                            });
                        }
                    }
                } catch (error) {
                    logger(error, "ContentManager.tsx (Load Teams context location)");
                }
                dispatch(setWorkingDirectories(directories));
            }
        } catch (error) {
            logger(error, "ContentManager.tsx (preloadTeamChannelLocationStructure)");
        }

        dispatch(setIsContentManagerContentLoading(false));
    }, []);

    const preloadLocationStructure = React.useCallback(async (account: IAuthAccount, preloadLocation: IPreloadLocation, oneDriveDetails: IOneDriveRootDetails): Promise<void> => {
        dispatch(setIsContentManagerContentLoading(true));

        try {
            let directoryBuilder: IDirectoryBuilder;
            if (preloadLocation.webUrl?.startsWith(account?.oneDriveWebUrl)) {
                if (oneDriveDetails) {
                    dispatch(setOneDriveDetails(oneDriveDetails));
                    directoryBuilder = new SearchedOneDriveDirectoryBuilder(account?.oneDriveWebUrl, oneDriveDetails.sharePointListId);
                }
            }
            else {
                directoryBuilder = new SearchedSharepointFoldersDirectoryBuilder([], [], preloadLocation.type, false);
                dispatch(setContentManagerDirectory(preloadLocation.isTeams ? ContentManagerDirectoryEnum.Teams : ContentManagerDirectoryEnum.SharePoint));
            }

            if (directoryBuilder) {
                const directories = await directoryBuilder.getFolderDirectories(preloadLocation);
                dispatch(resetWorkingDirectories(directories));
            }
        } catch (error) {
            logger(error, "ContentManager.tsx (preloadLocationStructure - DirectoryBuilder)");
        }

        dispatch(setIsContentManagerContentLoading(false));
    }, []);

    const setLandingPage = React.useCallback(async (account: IAuthAccount, userProfile: IUserProfile, tenantInfo: TenantState) => {
        if (userProfile?.settings?.landingPage !== LandingPageEnum.Default) {
            try {
                switch (userProfile.settings.landingPage) {
                    case LandingPageEnum.SharePoint:
                        if (userProfile.settings.enableSharePoint) {
                            dispatch(setContentManagerDirectory(ContentManagerDirectoryEnum.SharePoint));
                        }
                        break;
                    case LandingPageEnum.Teams:
                        if (userProfile.settings.enableTeams) {
                            dispatch(setContentManagerDirectory(ContentManagerDirectoryEnum.Teams));
                        }
                        break;
                    case LandingPageEnum.Exchange:
                        if (tenantInfo?.enableExchange) {
                            dispatch(setContentManagerDirectory(ContentManagerDirectoryEnum.Exchange));
                        }
                        break;
                    case LandingPageEnum.OneDrive:
                        if (userProfile.settings.enableOneDrive) {
                            let oneDriveDetails: IOneDriveRootDetails;
                            if (!oneDriveRootDetails) {
                                oneDriveDetails = await getOneDriveRootDetails();
                                dispatch(setOneDriveDetails(oneDriveDetails));
                            }

                            const locationItem = Initiallocations.find(x => x.name === ComponentTypes.OneDrive);
                            dispatch(setWorkingDirectories([{
                                type: locationItem.type,
                                id: locationItem.id,
                                directory: "",
                                displayName: locationItem.name,
                                webUrl: account.oneDriveWebUrl,
                                sharePointListId: oneDriveDetails ? oneDriveDetails?.sharePointListId : oneDriveRootDetails?.sharePointListId
                            }]));
                        }
                        break;
                    case LandingPageEnum.Tess:
                        if (tenantInfo?.isTessEnabled) {
                            dispatch(setContentManagerDirectory(ContentManagerDirectoryEnum.Tess));
                        }
                        break;
                    default:
                        break;
                }
            } catch (error) {
                logger(error, "ContentManager.tsx (setLandingPage)");
            }
        }
    }, []);

    const enableResize = React.useCallback(() => {
        setIsResizing(true);
    }, [setIsResizing]);

    const disableResize = React.useCallback(
        (e: MouseEvent) => {
            setIsResizing(false);

            try {
                switch (e.button) {
                    case 3: // Fourth button, typically the Browser Back button
                        history.back();
                        break;
                    case 4: // Fifth button, typically the Browser Forward button
                        history.forward();
                        break;
                }
            } catch (error) {
                logger(error, "ContentManager.tsx (Handle mouse back/forward buttons)");
            }
        },
        [setIsResizing]
    );

    const resize = React.useCallback(
        (e: MouseEvent) => {
            if (isResizing) {
                if (e.clientX >= 350) {
                    setWidth(e.clientX);
                }
            }
        },
        [isResizing, setWidth]
    );

    const handleNavigation = React.useCallback((ev: PopStateEvent) => {
        try {
            const historyState = ev.state;
            if (historyState !== null && historyState !== undefined) {
                if (Array.isArray(historyState)) {
                    dispatch(resetWorkingDirectories(historyState as IPageSettings[], false));
                    dispatch(toggleContentManagerReload());
                }
                else if (historyState) {
                    dispatch(resetWorkingDirectories(undefined, false));
                    dispatch(setContentManagerDirectory(historyState, false));
                    dispatch(setContentManagerSelectedHomeSection(ContentManagerHomeEnum.Pinned));
                }
                else {
                    dispatch(resetWorkingDirectories(undefined, false));
                    dispatch(resetContentManagerDirectory(false));
                }
                dispatch(setContentManagerSearchValue(""));
                dispatch(setContentManagerFilterValue(""));
            }
        } catch (error) {
            logger(error, "ContentManager.tsx (handleNavigation)");
        }
    }, [resetWorkingDirectories, resetContentManagerDirectory, setContentManagerSearchValue, setContentManagerFilterValue]);

    React.useEffect(() => {
        isSideMenuCollapsedRef.current = isSideMenuCollapsed;
    }, [isSideMenuCollapsed]);

    React.useEffect(() => {
        screenSizedRef.current = windowSize.screenSize;

        if (windowSize.screenSize === ScreenSize.XXSmall || windowSize.screenSize === ScreenSize.XSmall) {
            if (!isSideMenuCollapsedRef.current) {
                dispatch(setIsContentManagerSideMenuCollapsed(true));
            }
        } else {
            if (isSideMenuCollapsedRef.current) {
                dispatch(setIsContentManagerSideMenuCollapsed(false));
            }
        };
    }, [windowSize.screenSize]);

    React.useEffect(() => {
        if (currentDirectory) {
            dispatch(setContentManagerSelectedHomeSection(ContentManagerHomeEnum.None));
        }

        if (!isSideMenuCollapsedRef.current && (screenSizedRef.current === ScreenSize.XXSmall || screenSizedRef.current === ScreenSize.XSmall)) {
            dispatch(setIsContentManagerSideMenuCollapsed(true));
        }
    }, [currentDirectory]);

    React.useEffect(() => {
        if (!isSideMenuCollapsedRef.current && (screenSizedRef.current === ScreenSize.XXSmall || screenSizedRef.current === ScreenSize.XSmall)) {
            dispatch(setIsContentManagerSideMenuCollapsed(true));
        }
    }, [selectedHomeSection]);

    React.useEffect(() => {
        (async () => {
            try {
                dispatch(setMode(initialMode));
                dispatch(setUserAccount(account));
                if (await authModule.getDocubirdToken()) {
                    const preload: string = await StorageHelper.getItemAsync(contentManager.params.preloadLocation, true);
                    const preloadUrl: string = !preload && await StorageHelper.getItemAsync(contentManager.params.preloadURL, true);
                    if (!preload && !preloadUrl) {
                        try {
                            const authState: IAuthTokenResultDto = authModule.getPersistedAuthState();
                            const userProfile: IUserProfile = getLocalStorage(localStorageKeys.userProfile);
                            const tenantInfo: TenantState = getLocalStorage(localStorageKeys.tenantInfo);
                            if (userProfile && tenantInfo) {
                                userProfile.isLoadedFromCache = true;
                                dispatch(setUserProfile(userProfile));
                                dispatch(setTenantInfo(tenantInfo));
                                const oneDriveDetails: IOneDriveRootDetails = getLocalStorage(localStorageKeys.oneDriveDetails);
                                if (oneDriveDetails) {
                                    dispatch(setOneDriveDetails(oneDriveDetails));
                                }
                                if (userProfile?.settings?.landingPage) {
                                    await setLandingPage(account, userProfile, tenantInfo);
                                }
                                else {
                                    registerHistoryEntry(ContentManagerDirectoryEnum.Home, true);
                                }
                                const hooks: IHookResult[] = getLocalStorage(localStorageKeys.automationHooks);
                                if (Array.isArray(hooks) && hooks.length > 0) {
                                    CacheHelper.set(DocubirdCache.hooks, hooks);
                                    CacheHelper.set(DocubirdCache.signingEnabled, hooks.filter(h => h.typeId === HookType.Sign).length > 0);
                                    CacheHelper.set(DocubirdCache.signalREnabled, hooks.findIndex(h => h.typeId === HookType.SharePointItemCreate || h.typeId === HookType.SharePointItemMetadataEdit) !== -1);
                                }
                                setUserAuthData(authState);
                                setIsLoading(false);
                            }
                        } catch (error) {
                            logger(error, "contentManagerTeams.tsx Init from cache");
                        }
                    }

                    const [authResult, userProfile, tenantInfo] = await Promise.all([authModule.signIn(), loadUserProfile(account), loadTenantInfo(account), setSigningEnabled(account?.tenantId)]);

                    const oneDriveDetails: IOneDriveRootDetails = await getOneDriveRootDetails();
                    if (oneDriveDetails) {
                        dispatch(setOneDriveDetails(oneDriveDetails));
                        setLocalStorage(localStorageKeys.oneDriveDetails, oneDriveDetails);
                    }

                    const isLocationSet: boolean = await preloadLocation(account, oneDriveDetails, preload, preloadUrl);
                    if (!isLocationSet) {
                        if (userProfile?.settings?.landingPage) {
                            await setLandingPage(account, userProfile, tenantInfo);
                        }
                        else {
                            registerHistoryEntry(ContentManagerDirectoryEnum.Home, true);
                        }
                    }
                    setUserAuthData(authResult);
                }
                else {
                    authModule.signOut();
                }
            } catch (error) {
                logger(error, "ContentManager.tsx (Effect)");
            }
            setIsLoading(false);
        })()
    }, []);

    React.useEffect(() => {
        if (tenantInfo.tenantId && account?.accountId) {
            getEmailToMetaDataMapping(account?.accountId, tenantInfo.emailToMetadataMapping);
        }
    }, [tenantInfo, account?.accountId]);

    React.useEffect(() => {
        document.addEventListener("mousemove", resize);
        document.addEventListener("mouseup", disableResize);

        return () => {
            document.removeEventListener("mousemove", resize);
            document.removeEventListener("mouseup", disableResize);
        };
    }, [disableResize, resize]);

    React.useEffect(() => {
        window.addEventListener("popstate", handleNavigation);

        return () => {
            window.removeEventListener("popstate", handleNavigation);
        };
    }, []);

    return (
        <>
            {isLoading && <Loader label={languageStrings.LoadingProfile} />}
            {!isLoading && !user.isActivated &&
                <Stack style={{ height: "100vh" }} verticalAlign="center" horizontalAlign="center">
                    <DocumentCard styles={{
                        root: {
                            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                            width: "30%",
                            minWidth: "265px",
                            maxWidth: "600px"
                        }
                    }}>
                        <DocumentCardDetails>
                            <TrialRequest isLoadingProfile={isLoading} licenseType={user.licenseType} accountName={userAuthData?.account?.displayName} />
                        </DocumentCardDetails>
                    </DocumentCard>
                </Stack>
            }
            {userAuthData && user.isActivated &&
                <>
                    <div className={mode === ModeEnum.contentManagerAdvancedSearch ? "none" : "xb-app"}>
                        <ContentManagerHeader account={account} />
                        <Stack horizontal tokens={outerStackTokens}>
                            <Stack.Item styles={isSideMenuCollapsed ? directoryContainerCollapsedStackItemStyles : directoryContainerStackItemStyles}>
                                <ContentBodyDirectory />
                            </Stack.Item>
                            {!isSideMenuCollapsed && <div style={draggableBorderStyles} onMouseDown={enableResize} />}
                            <Stack.Item grow>
                                <ContentManagerBody />
                            </Stack.Item>
                        </Stack>
                    </div>
                    {mode === ModeEnum.contentManagerAdvancedSearch &&
                        <AdvancedSearch
                            account={account}
                            siteId={currentDirectory?.siteId ?? workingDir.slice().reverse().find(dir => dir.siteId)?.siteId}
                            hubId={currentDirectory?.isHub && currentDirectory?.hubId ? currentDirectory.hubId : undefined}
                            isInConentMangerMode={true}
                            userProfile={user}
                            tenantInfo={tenantInfo}
                            teamGroupId={currentDirectory?.teamGroupId ? currentDirectory.teamGroupId : undefined}
                            teamDriveId={currentDirectory?.teamDriveId ? currentDirectory.teamDriveId : undefined}
                        />
                    }
                </>
            }
            {/* {isOverlayVisible && <div className="xb-overlay"></div>} */}
        </>
    )
};